/**
 * Como o card anda pelo quadro com o PharuCut — a leitura da configuração
 * em frases e avisos, para a tela "Configurar colunas do kanban".
 *
 * Espelha `solicitacoes.acompanhamento_cut` do backend:
 * - o acontecimento escolhido numa coluna TRAZ o card para ela;
 * - os acontecimentos têm ordem (a ordem da lista que o backend manda);
 * - para um acontecimento, vale a PRIMEIRA coluna do quadro que o usa;
 * - sem coluna para um acontecimento, o card fica na do anterior;
 * - coluna que ancora só situações que o lote não tem (Aguardando
 *   autorização, Atendida, Recusada, Cancelada) nunca recebe o card.
 */

const SITUACOES_QUE_O_LOTE_TEM = ["AUTORIZADA", "EM_ANDAMENTO"];

const nome = (coluna) => `"${(coluna.titulo || "").trim() || "sem título"}"`;

/** A coluna aceita card trazido pelo PharuCut? */
export function recebeDoPharuCut(coluna) {
  const ancoradas = coluna.situacoes || [];
  return ancoradas.length === 0 || ancoradas.some((s) => SITUACOES_QUE_O_LOTE_TEM.includes(s));
}

function frasePrincipal(coluna, evento, nivel, seguinte) {
  const chegada = nivel === 0
    ? `O card volta para cá quando ${evento.quando}`
    : `O card chega aqui quando ${evento.quando}`;
  if (!seguinte) return `${chegada} e fica aqui até alguém arrastar.`;
  return `${chegada} e segue para ${nome(seguinte.coluna)} quando ${seguinte.evento.quando}.`;
}

/**
 * Para cada coluna: `{ frase, avisos }`. `frase` explica o que acontece;
 * `avisos` aponta configuração que não funciona como parece.
 */
export function analisarFluxo(colunas, eventos) {
  const resultado = Object.fromEntries((colunas || []).map((c) => [c.id, { frase: "", avisos: [] }]));
  const lista = eventos || [];
  const nivelDe = (valor) => lista.findIndex((e) => e.value === valor);

  const mapeadas = (colunas || [])
    .map((coluna) => ({ coluna, nivel: nivelDe(coluna.evento_cut) }))
    .filter((m) => m.nivel >= 0)
    .map((m) => ({ ...m, evento: lista[m.nivel] }));
  if (mapeadas.length === 0) return resultado;

  // Para cada acontecimento vale a primeira coluna do quadro que o usa.
  const efetivas = [];
  mapeadas.forEach((m) => {
    const primeira = efetivas.find((e) => e.nivel === m.nivel);
    if (primeira) {
      resultado[m.coluna.id].avisos.push(
        `${nome(primeira.coluna)} já usa este acontecimento e vem antes no quadro: esta coluna nunca recebe o card. Escolha outro acontecimento ou deixe em branco.`,
      );
      return;
    }
    efetivas.push(m);
  });

  efetivas.forEach((m, i) => {
    const r = resultado[m.coluna.id];
    if (!recebeDoPharuCut(m.coluna)) {
      r.avisos.push(
        "Esta coluna é de uma situação que o PharuCut não muda (aguardando autorização, atendida, recusada ou cancelada): ela nunca recebe o card. Use uma coluna de Autorizada, Em andamento ou sem status.",
      );
      return;
    }
    const seguinte = efetivas
      .filter((o) => o.nivel > m.nivel)
      .sort((a, b) => a.nivel - b.nivel)[0];
    r.frase = frasePrincipal(m.coluna, m.evento, m.nivel, seguinte);
    const anterior = efetivas.slice(0, i).find((o) => o.nivel > m.nivel);
    if (anterior) {
      r.avisos.push(
        `Está depois de ${nome(anterior.coluna)} no quadro, mas este acontecimento vem antes no PharuCut: na tela o card andaria para trás. Arraste as colunas para a ordem dos acontecimentos.`,
      );
    }
  });

  (colunas || []).forEach((c) => {
    if (nivelDe(c.evento_cut) < 0) {
      resultado[c.id].frase = "Não muda sozinha: o card só entra e sai daqui arrastando.";
    }
  });
  return resultado;
}

/** Total de avisos, para o resumo acima do botão Salvar. */
export function contarAvisos(analise) {
  return Object.values(analise || {}).reduce((n, r) => n + r.avisos.length, 0);
}
